import React, { forwardRef, useImperativeHandle } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import PlaceSearch from "../../../component/location/PlaceSearch";
import {
  formatFileURL,
  uploadFile,
  validateDocumentFileType,
  validateImageFileType,
} from "../../../utils/helper/appHelpers";
import { showMessage } from "../../../utils/toast/globalToast";
import { _toastVariants, FILE_UPLOAD_DIR } from "../../../utils/Constant";

const CompanyTab = forwardRef(({ company }, ref) => {

  const ServiceSchema = Yup.object().shape({
    businessName: Yup.string().required("Business Name is required"),
    businessLocation: Yup.object().shape({
      address: Yup.string().required("Address is required"),
    }),
    businessLogo: Yup.mixed().nullable(),
    registrationDocuments: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required("Document type is required"),
        expiryDate: Yup.date().nullable(),
        attachments: Yup.array().nullable(),
      })
    ),
  });


  const mergedDocs = [
    ...(company?.documents || []),
    ...(company?.idProof || []),
  ];

  const formik = useFormik({
    enableReinitialize: true, 
    initialValues: {
      businessName: company?.name || "",
      businessLocation: {
        address: company?.location
          ? `${company.location.addressLine1 || ""} ${
              company.location.city || ""
            }, ${company.location.state || ""}, ${
              company.location.country || ""
            } - ${company.location.pincode || ""}`
          : "",
      },
      businessLogo: company?.logo || null,
      registrationDocuments:
        mergedDocs.length > 0
          ? mergedDocs.map((doc) => ({
              name: doc.name || "",
              expiryDate: doc.expiryDate || "",
              attachments: doc.attachments || [],
            }))
          : [{ name: "", expiryDate: "", attachments: [] }],
    },
    validationSchema: ServiceSchema,
    onSubmit: () => {},
  });

  const { values, errors, touched, setFieldValue } = formik;

  const handleLogoUpload = (e) => {
    const [file] = e.target.files;
    if (!file) return;

    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      showMessage({
        variant: _toastVariants.Error,
        message: "File size should not exceed 2 MB",
      });
      e.target.value = "";
      return;
    }

    if (!validateImageFileType(file.type)) {
      showMessage({
        variant: _toastVariants.Error,
        message: "Unsupported file type",
      });
      e.target.value = "";
      return;
    }

    setFieldValue("businessLogo", file);
    e.target.value = ""; 
  };


  const handleDocumentUpload = async (e, docIndex) => {
    const [file] = e.target.files;
    if (!file) return;

    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      showMessage({
        variant: _toastVariants.Error,
        message: "File size should not exceed 2 MB",
      });
      e.target.value = "";
      return;
    }

    if (!validateDocumentFileType(file.type)) {
      showMessage({
        variant: _toastVariants.Error,
        message: "Unsupported file type",
      });
      e.target.value = "";
      return;
    }

    const newDocs = [...values.registrationDocuments];
    newDocs[docIndex] = {
      ...newDocs[docIndex],
      attachments: [...newDocs[docIndex].attachments, file],
    };
    setFieldValue("registrationDocuments", newDocs);
    e.target.value = "";
  };


  useImperativeHandle(ref, () => ({
  async submitForm() {
    let uploadedLogo = values.businessLogo;

    if (values.businessLogo instanceof File) {
      const uploaded = await uploadFile(
        values.businessLogo,
        FILE_UPLOAD_DIR.USER
      );
       uploadedLogo = typeof uploaded === "string" ? uploaded : uploaded?.fileUrl || uploaded?.data?.url || null;
       if (!uploadedLogo && typeof values.businessLogo === "string") {
          uploadedLogo = values.businessLogo;
        }
    }
    const uploadedDocs = await Promise.all(
      (values.registrationDocuments || []).map(async (doc) => {
        const uploadedFiles = await Promise.all(
          (doc.attachments || []).map(async (file) => {
            if (file instanceof File) {
              const uploaded = await uploadFile(file, FILE_UPLOAD_DIR.USER);

              const uploadedPath =
                typeof uploaded === "string"
                  ? uploaded
                  : uploaded?.fileUrl || uploaded?.data?.url || null;

              return uploadedPath;
            }
            return file;
          })
        );

        return { ...doc, attachments: uploadedFiles };
      })
    );


    const documents = uploadedDocs.filter(
      (doc) => doc.name && doc.name !== "AADHAR_CARD"
    );
    const idProof = uploadedDocs.filter(
      (doc) => doc.name && doc.name === "AADHAR_CARD"
    );

    const locationData = values.businessLocation;

    const parsedLocation = {
      city: locationData?.city || company?.location?.city || null,
      state: locationData?.state || company?.location?.state || null,
      country: locationData?.country || company?.location?.country || "India",
      pincode: locationData?.pincode || company?.location?.pincode || null,
      latitude: locationData?.latitude || company?.location?.latitude || null,
      longitude: locationData?.longitude || company?.location?.longitude || null,
      addressLine1:
        locationData?.addressLine1 ||
        (typeof locationData === "string" ? locationData : "") ||
        company?.location?.addressLine1 ||
        null,
    };


    const finalData = {
      businessInfo: {
        name: values.businessName,
        logo: uploadedLogo,
        location: parsedLocation,
        documents,
        idProof,
      },
    };

    return finalData; 
  },

  getValues: () => formik.values,
}));


  return (
    <div className="row mt-4">
      {/* Business Name */}
      <div className="col-md-6">
        <div className="form-group">
          <label>
            Business Name <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            placeholder="Enter Business Name"
            value={values.businessName}
            onChange={(e) => setFieldValue("businessName", e.target.value)}
          />
          {touched.businessName && errors.businessName && (
            <div className="text-danger">{errors.businessName}</div>
          )}
        </div>
      </div>

      {/* Business Location */}
      <div className="col-md-6">
        <div className="form-group">
          <label>
            Business Location <span className="text-danger">*</span>
          </label>
            <PlaceSearch
              placeholder="Enter Address"
              type="address"
              defaultValue={values.businessLocation?.address}
              onselect={(place) => {
                setFieldValue("businessLocation", {
                  addressLine1:
                    place.formatted_address ||
                    place.name ||
                    place.address ||
                    place.inputValue || 
                    "",
                  city: place.city || "",
                  state: place.state || "",
                  country: place.country || "",
                  pincode: place.pincode || "",
                  latitude: place.lat || null,
                  longitude: place.lng || null,
                });
              }}
            />

          {touched.businessLocation?.address &&
            errors.businessLocation?.address && (
              <div className="text-danger">
                {errors.businessLocation?.address}
              </div>
            )}
        </div>
      </div>

      {/* Business Logo */}
      <div className="col-md-6">
        <div className="form-group">
          <label>Business Logo</label>
          <input
            type="file"
            id="businessLogoUpload"
            className="d-none"
            onChange={handleLogoUpload}
            accept="image/*"
          />
          <label
            htmlFor="businessLogoUpload"
            className="form-control"
            style={{ cursor: "pointer" }}
          >
            + Upload
          </label>

          {values.businessLogo && (
            <div className="mt-2 uploaddiv position-relative">
              <img
                src={
                  typeof values.businessLogo === "string"
                    ? formatFileURL(values.businessLogo)
                    : URL.createObjectURL(values.businessLogo)
                }
                alt="business logo"
                className="uploadimages"
              />
              <button
                type="button"
                className="closeimg"
                onClick={() => setFieldValue("businessLogo", null)}
              >
                ✕
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Business Documents */}
      <div className="col-md-12 mt-4">
        <label className="label-input f-18 mb-0">Business Documents</label>
        {values.registrationDocuments.map((doc, docIndex) => (
          <div key={docIndex} className="card mb-3">
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-md-6">
                  <label>Type</label>
                  <input
                    type="text"
                    className="form-control"
                    value={doc.name}
                    onChange={(e) => {
                      const newDocs = [...values.registrationDocuments];
                      newDocs[docIndex] = {
                        ...newDocs[docIndex],
                        name: e.target.value,
                      };
                      setFieldValue("registrationDocuments", newDocs);
                    }}
                  />
                </div>

                <div className="col-md-6">
                  <label>Expiry Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={doc.expiryDate}
                    onChange={(e) => {
                      const newDocs = [...values.registrationDocuments];
                      newDocs[docIndex] = {
                        ...newDocs[docIndex],
                        expiryDate: e.target.value,
                      };
                      setFieldValue("registrationDocuments", newDocs);
                    }}
                  />
                </div>

                {/* Document Upload */}
                <div className="col-md-6 mt-2">
                  <label>Document</label>
                  <input
                    type="file"
                    id={`uploadDoc-${docIndex}`}
                    className="d-none"
                    onChange={(e) => handleDocumentUpload(e, docIndex)}
                    accept="image/*,application/pdf"
                  />
                  <label
                    htmlFor={`uploadDoc-${docIndex}`}
                    className="form-control mb-0"
                    style={{ cursor: "pointer" }}
                  >
                    + Add
                  </label>
                </div>

                {/* Preview */}
                <div className="col-md-12 mt-3">
                  {doc.attachments?.length > 0 && (
                    <div className="d-flex flex-wrap gap-3">
                      {doc.attachments.map((file, index) => (
                        <div
                          key={index}
                          className="mt-2 uploaddiv position-relative"
                        >
                          <img
                            src={
                              file instanceof File
                                ? URL.createObjectURL(file)
                                : formatFileURL(file)
                            }
                            className="uploadimages"
                            alt="document"
                          />
                          <button
                            type="button"
                            className="closeimg"
                            onClick={() => {
                              const newDocs = [
                                ...values.registrationDocuments,
                              ];
                              newDocs[docIndex] = {
                                ...newDocs[docIndex],
                                attachments:
                                  newDocs[docIndex].attachments.filter(
                                    (_, i) => i !== index
                                  ),
                              };
                              setFieldValue("registrationDocuments", newDocs);
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}

        <div className="text-end mt-2">
          <button
            type="button"
            className="btn btn-success"
            onClick={() =>
              setFieldValue("registrationDocuments", [
                ...values.registrationDocuments,
                { name: "", expiryDate: "", attachments: [] },
              ])
            }
          >
            + Add More Document
          </button>
        </div>
      </div>
    </div>
  );
});

export default CompanyTab;
